import React, { useState } from 'react'
import { Truck, MapPin } from 'lucide-react'
import { useTaste } from '../contexts/TasteContext'
import { CAMPAIGN } from '../config/campaign'

// Honest region buckets derived from the live taste_shipping_rates table
// (origin 91352). Ranges span both weight tiers (2-pack = 10 lb, 4/6/8 = 14 lb)
// so every pack's real cost falls inside the stated range — no undershoot.
const REGIONS = [
  { label: 'West Coast', detail: 'CA · NV · AZ', range: '$19–$28' },
  { label: 'Mountain & Southwest', detail: 'OR · WA · ID · TX · CO · UT · NM', range: '$28–$50' },
  { label: 'Midwest & South', detail: 'Central & Southeast states', range: '$54–$75' },
  { label: 'Northeast & Far', detail: 'NY · New England · FL', range: '$61–$78' },
] as const

const ShippingEstimator: React.FC = () => {
  const { selectedPack, hasCaviar } = useTaste()

  const [zip, setZip] = useState('')
  const [estimate, setEstimate] = useState<number | null>(null)
  const [estimated, setEstimated] = useState(false) // true => ZIP prefix unknown, fell back to zone 8
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleEstimate = async () => {
    setError(null)
    setEstimate(null)
    if (!/^\d{5}$/.test(zip)) {
      setError('Enter a 5-digit ZIP code')
      return
    }
    setLoading(true)
    try {
      const res = await fetch('/.netlify/functions/get-taste-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Default to the 6-pack if nothing is selected (selectedPack defaults to
        // 6 in context, but stay defensive). hasCaviar/state don't affect the
        // shipping line, so we pass what we have.
        body: JSON.stringify({
          packSize: selectedPack?.size ?? 6,
          hasCaviar,
          state: '',
          zip,
        }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data || typeof data.shippingCost !== 'number') {
        throw new Error(data?.error || 'Could not fetch a shipping estimate')
      }
      setEstimate(data.shippingCost)
      setEstimated(Boolean(data.shippingEstimated))
    } catch (e: any) {
      setError(e.message || 'Could not fetch a shipping estimate')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="relative z-10" style={{ backgroundColor: '#0d0d0d' }}>
      <div className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-3xl md:text-4xl font-bold !text-white font-heading">
            Know Your Shipping Up Front
          </h2>
          <p className="mt-3 !text-white/60 font-body max-w-2xl mx-auto">
            Everything ships frozen via UPS 2-Day. Here's roughly what shipping runs by
            region — or check your exact rate by ZIP. No surprises at checkout.
          </p>
        </div>

        {/* Region grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8">
          {REGIONS.map((r) => (
            <div
              key={r.label}
              className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 text-center flex flex-col items-center"
            >
              <MapPin className="w-6 h-6 mb-3 !text-[#2563EB]" strokeWidth={1.75} />
              <div className="text-base md:text-lg font-bold !text-white font-heading mb-1">
                {r.label}
              </div>
              <div className="text-xs !text-white/50 font-body mb-3 leading-snug">
                {r.detail}
              </div>
              <div className="text-xl md:text-2xl font-bold !text-[#2563EB] font-heading mt-auto">
                {r.range}
              </div>
            </div>
          ))}
        </div>

        {/* ZIP estimator */}
        <div className="max-w-xl mx-auto bg-white/5 border border-[#2563EB]/40 rounded-2xl p-5 md:p-7">
          <div className="flex items-center gap-2 justify-center mb-4">
            <Truck className="w-5 h-5 !text-[#2563EB]" aria-hidden="true" />
            <span className="text-sm md:text-base font-semibold !text-white font-heading">
              Check your exact shipping
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              value={zip}
              onChange={(e) => {
                setZip(e.target.value.replace(/\D/g, '').slice(0, 5))
                setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEstimate()
              }}
              placeholder="Enter your ZIP"
              maxLength={5}
              className="flex-1 px-4 py-3 border-2 border-white/20 rounded-xl focus:border-[#2563EB] focus:outline-none bg-white/10 !text-white text-lg placeholder-white/40"
            />
            <button
              onClick={handleEstimate}
              disabled={loading}
              className="flex-shrink-0 min-h-[44px] bg-[#2563EB] hover:bg-[#1D4ED8] !text-white font-bold py-3.5 px-6 rounded-xl text-base font-heading transition-colors duration-300 disabled:opacity-70"
            >
              {loading ? 'Checking…' : 'Estimate'}
            </button>
          </div>

          {error && <p className="mt-3 text-sm text-red-400 text-center">{error}</p>}

          {estimate !== null && !error && (
            <div className="mt-4 text-center">
              <p className="!text-white/70 font-body">
                UPS 2-Day to <span className="font-semibold !text-white">{zip}</span>:{' '}
                <span className="text-xl font-bold !text-[#2563EB]">${estimate.toFixed(2)}</span>
              </p>
              <p className="mt-1 text-xs !text-white/40">
                {estimated
                  ? 'Estimated for your area — exact rate confirmed at checkout.'
                  : `Frozen UPS 2-Day · ships by ${CAMPAIGN.shipDateDisplay}.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default ShippingEstimator
